import { EventBusEvent } from '@core/types';

type EventHandler<T = any> = (payload: T) => void;
type UnsubscribeFunction = () => void;

export class EventBus {
    private listeners = new Map<string, Set<EventHandler>>();
    private history: EventBusEvent[] = [];
    private maxHistorySize = 100;

    // Subscribe to an event
    on<T = any>(eventType: string, handler: EventHandler<T>): UnsubscribeFunction {
        if (!this.listeners.has(eventType)) {
            this.listeners.set(eventType, new Set());
        }

        const handlers = this.listeners.get(eventType)!;
        handlers.add(handler);

        // Return unsubscribe function
        return () => {
            handlers.delete(handler);
            if (handlers.size === 0) {
                this.listeners.delete(eventType);
            }
        };
    }

    // Subscribe to an event only once
    once<T = any>(eventType: string, handler: EventHandler<T>): UnsubscribeFunction {
        const unsubscribe = this.on(eventType, (payload: T) => {
            handler(payload);
            unsubscribe();
        });

        return unsubscribe;
    }

    // Emit an event
    emit<T = any>(eventType: string, payload?: T): void {
        const event: EventBusEvent = {
            type: eventType,
            payload,
            timestamp: new Date()
        };

        // Add to history
        this.history.push(event);
        if (this.history.length > this.maxHistorySize) {
            this.history = this.history.slice(-this.maxHistorySize);
        }

        // Notify listeners
        const handlers = this.listeners.get(eventType);
        if (handlers) {
            handlers.forEach((handler) => {
                try {
                    handler(payload);
                } catch (error) {
                    console.error(`[EventBus] Error in handler for event '${eventType}':`, error);
                }
            });
        }

        console.log(`[EventBus] Emitted '${eventType}'`, payload);
    }

    // Remove all listeners for an event type
    off(eventType: string): void {
        this.listeners.delete(eventType);
    }

    // Remove all listeners
    clear(): void {
        this.listeners.clear();
        this.history = [];
    }

    // Get event history
    getHistory(eventType?: string): EventBusEvent[] {
        if (eventType) {
            return this.history.filter((event) => event.type === eventType);
        }
        return [...this.history];
    }

    // Get active event types
    getEventTypes(): string[] {
        return Array.from(this.listeners.keys());
    }

    // Get listener count for an event type
    getListenerCount(eventType: string): number {
        return this.listeners.get(eventType)?.size || 0;
    }
}

// Singleton instance
export const eventBus = new EventBus();

// ─── Page action registry ─────────────────────────────────────────────────────
// Each value maps to one action button in NewPageTitleBar.
// To add a new button: add an entry here + call useRegisterAction(PageAction.X, fn)
// in your page. Nothing else in the architecture needs to change.
export enum PageAction {
    REFRESH = 'refresh',
    EXPORT = 'export',
    DOWNLOAD = 'download'
}

export const EventTypes = {
    // Authentication
    USER_LOGGED_IN: 'user:logged-in',
    USER_LOGGED_OUT: 'user:logged-out',
    USER_UPDATED: 'user:updated',

    // Navigation
    ROUTE_CHANGED: 'route:changed',

    // Data updates
    DATA_REFRESHED: 'data:refreshed',
    ORGANISATION_CHANGED: 'organisation:changed',
    SHOP_UPDATED: 'shop:updated',
    MODULE_UPDATED: 'module:updated',

    // UI updates
    SIDEBAR_TOGGLED: 'ui:sidebar-toggled',
    THEME_CHANGED: 'ui:theme-changed',
    LANGUAGE_CHANGED: 'ui:language-changed',

    // Widgets
    WIDGETS_RELOAD: 'widgets:reload',
    WIDGET_ERROR: 'widget:error',
    WIDGET_ADD: 'widget:add',

    // Notifications
    NOTIFICATION_SHOWN: 'notification:shown',
    NOTIFICATION_DISMISSED: 'notification:dismissed',

    // Plugin system
    PLUGIN_LOADED: 'plugin:loaded',
    PLUGIN_UNLOADED: 'plugin:unloaded',

    // Error handling
    API_ERROR: 'api:error',
    GLOBAL_ERROR: 'global:error',

    // ─── Page lifecycle ───────────────────────────────────────────────────────
    // Emitted by RefreshContext when any registered action starts / ends.
    // Consumed only by MainLayout to drive the global LoadingOverlay.
    // Payload shape: { action: PageAction }
    PAGE_LOADING: 'page:loading',
    PAGE_LOADED: 'page:loaded',
    PAGE_ERROR: 'page:error'
} as const;
